/**
 * The key to Geometri Güveni, and the part of it that is not a colour.
 *
 * A trust view whose only output is a hue is a trust view that says nothing to
 * a colour-blind reader, nothing in a greyscale screenshot and nothing to
 * anybody who cannot remember which blue meant which. So every class here
 * carries its name, its sentence and its share of the model, and the share is
 * a real count of vertices rather than an impression.
 *
 * Classes with nothing in them are dropped rather than listed at zero - a
 * legend of four rows where three read "%0" trains people to stop reading it -
 * except that "no completed surface" is stated outright when the model has
 * none, because that is the reassurance somebody in a visual-completion
 * project is actually looking for.
 */

import type { Mesh } from "./glb";
import { provenanceShares, provenanceSwatch, provenanceIsUnrecorded } from "./viewerProvenance";

const percent = (share: number): string =>
  share > 0 && share < 0.01 ? "<%1" : `%${Math.round(share * 100)}`;

export function ProvenanceLegend({ mesh }: { mesh: Mesh }) {
  const shares = provenanceShares(mesh);
  const present = shares.filter((entry) => entry.vertices > 0);
  const completed = shares.find((entry) => entry.value === "ai_completed");

  return (
    <div className="viewer__legend" role="group" aria-label="Geometri güveni açıklaması">
      <h3 className="viewer__legend-title">Geometri Güveni</h3>
      {provenanceIsUnrecorded(mesh) ? (
        <p className="viewer__legend-note">
          Bu model, yüzeyin nereden geldiği kaydedilmeye başlanmadan önce üretilmiş.
          Kaydı olmaması, yapay olarak üretildiği anlamına gelmez.
        </p>
      ) : (
        completed?.vertices === 0 && (
          <p className="viewer__legend-note">
            Bu modelde yapay zekâ ile tamamlanmış yüzey yok.
          </p>
        )
      )}
      <ul className="viewer__legend-list">
        {present.map((entry) => (
          <li key={entry.value} className="viewer__legend-row">
            <span
              className="viewer__legend-swatch"
              style={{ background: provenanceSwatch(entry.value) }}
              aria-hidden="true"
            />
            <span className="viewer__legend-body">
              <span className="viewer__legend-label">
                {entry.label} — {percent(entry.share)}
              </span>
              <span className="viewer__legend-detail">{entry.note}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
